// Generated by TaroBase SDK Generator: 2026-04-14T11:42:05.668Z
// Collection: pinnedMessages/$pinId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Pinned messages for Danger Room chat channels. Moderators (admin, founder, team members) can pin/unpin important messages that display at the top of each channel.
 */

export interface PinnedMessagesRequest {
  messageId: string;
  channel: string;
  pinnedBy: string;
  pinnedAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface PinnedMessagesResponse {
  messageId: string;
  channel: string;
  pinnedBy: string;
  pinnedAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a PinnedMessages operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildPinnedMessages(pinId: string, data?: PinnedMessagesRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `pinnedMessages/${pinId}`, document: doc };
}

/**
 * Admin, founder, or any team member (wallet exists in teamMembers collection) can pin a message. The $pinId should be the messageId itself (one pin per message). pinnedBy must be set to the caller's wallet address. All fields (messageId, channel, pinnedBy, pinnedAt) are immutable after creation. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setPinnedMessages(pinId: string, data?: PinnedMessagesRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`pinnedMessages/${pinId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting PinnedMessages: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read pinned messages. Used to display pinned messages at the top of each Danger Room channel.
   (Get Single Item)
 */
export async function getPinnedMessages(pinId: string): Promise<PinnedMessagesResponse | null> {
  try {
    const result = await get(`pinnedMessages/${pinId}`);
    return result as PinnedMessagesResponse | null;
  } catch (error) {
    console.error(`Error getting PinnedMessages: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single PinnedMessages document. (
  Read Operation Details: Anyone can read pinned messages. Used to display pinned messages at the top of each Danger Room channel.
  )
 */
export function subscribePinnedMessages(callback: (data: PinnedMessagesResponse | null) => void, pinId: string): Promise<() => Promise<void>> {
  return subscribe(`pinnedMessages/${pinId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as PinnedMessagesResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to PinnedMessages at path '${`pinnedMessages/${pinId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many PinnedMessages items from collection pinnedMessages
 
  Read Operation Details: Anyone can read pinned messages. Used to display pinned messages at the top of each Danger Room channel.
  
 */
export async function getManyPinnedMessages(filter: string = ""): Promise<PinnedMessagesResponse[]> {
  try {
    const results = await get("pinnedMessages", { prompt: filter });
    return Array.isArray(results) ? results as PinnedMessagesResponse[] : [];
  } catch (error) {
    console.error(`Error getting PinnedMessages collection at path 'pinnedMessages': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in PinnedMessages collection at pinnedMessages
 
  Read Operation Details: Anyone can read pinned messages. Used to display pinned messages at the top of each Danger Room channel.
  
 */
export function subscribeManyPinnedMessages(callback: (data: PinnedMessagesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("pinnedMessages", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as PinnedMessagesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to PinnedMessages collection at path '${"pinnedMessages"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all PinnedMessages items from collection pinnedMessages
 
  Read Operation Details: Anyone can read pinned messages. Used to display pinned messages at the top of each Danger Room channel.
  
 */
export async function getAllPinnedMessages(filter: string = ""): Promise<PinnedMessagesResponse[]> {
  try {
    const results = await get("pinnedMessages", { prompt: filter });
    return Array.isArray(results) ? results as PinnedMessagesResponse[] : [];
  } catch (error) {
    console.error(`Error getting PinnedMessages collection at path 'pinnedMessages': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in PinnedMessages collection at pinnedMessages
 
  Read Operation Details: Anyone can read pinned messages. Used to display pinned messages at the top of each Danger Room channel.
  
 */
export function subscribeAllPinnedMessages(callback: (data: PinnedMessagesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("pinnedMessages", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as PinnedMessagesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to PinnedMessages collection at path '${"pinnedMessages"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count PinnedMessages items in collection pinnedMessages.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countPinnedMessages(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("pinnedMessages", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting PinnedMessages at path 'pinnedMessages': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on PinnedMessages items in collection pinnedMessages.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregatePinnedMessages(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("pinnedMessages", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating PinnedMessages at path 'pinnedMessages': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Admin, founder, or any team member can unpin a message by deleting the pin record.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deletePinnedMessages(pinId: string): Promise<boolean> {
  try {
    const succeeded = await set(`pinnedMessages/${pinId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting PinnedMessages: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for PinnedMessages for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeletePinnedMessages(pinId: string): DocumentOperation {
  return { path: `pinnedMessages/${pinId}`, document: null };
}

